import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Plus, Pencil, Trash2, Package, ExternalLink, DollarSign, Upload, Image, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductManagement = () => {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageSource, setImageSource] = useState('url'); // 'url' or 'upload'
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    link: '',
    price: '',
    condition_ids: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, conditionsRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/conditions`)
      ]);
      setProducts(productsRes.data);
      setConditions(conditionsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      image_url: '',
      link: '',
      price: '',
      condition_ids: []
    });
    setEditingProduct(null);
    setImageSource('url');
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description || '',
        image_url: product.image_url || '',
        link: product.link || '',
        price: product.price || '',
        condition_ids: product.condition_ids || []
      });
      // Check if it's an uploaded image
      if (product.image_url && product.image_url.includes('/api/uploads/')) {
        setImageSource('upload');
      } else {
        setImageSource('url');
      }
    } else {
      resetForm();
    }
    setIsOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WebP');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Use the full URL for the uploaded image
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.image_url}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to upload image: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, formData);
        toast.success(t('update') + '!');
      } else {
        await axios.post(`${API}/products`, formData);
        toast.success(t('create') + '!');
      }
      fetchData();
      setIsOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('delete') + '!');
      fetchData();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product: ' + (error.response?.data?.detail || error.message));
    }
  };

  const toggleCondition = (conditionId) => {
    setFormData(prev => ({
      ...prev,
      condition_ids: prev.condition_ids.includes(conditionId)
        ? prev.condition_ids.filter(id => id !== conditionId)
        : [...prev.condition_ids, conditionId]
    }));
  };

  const getConditionName = (condition) => {
    if (language === 'pt' && condition.name_pt) return condition.name_pt;
    return condition.name;
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="product-management">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <Package className="w-6 h-6 text-primary" />
              <h1 className="text-3xl font-bold">{t('productsTitle')}</h1>
            </div>
            <p className="text-muted-foreground mt-1">{t('manageProductRecommendations')}</p>
          </div>
          
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" onClick={() => handleOpenDialog()} data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('addProduct')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? t('editProduct') : t('newProduct')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-5 mt-4">
                <div className="space-y-2">
                  <Label>{t('productName')}</Label>
                  <Input
                    placeholder="e.g., Ibuprofen 200mg"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="product-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('productDescription')}</Label>
                  <Textarea
                    placeholder="Brief product description..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="product-description-input"
                  />
                </div>

                {/* Image input with tabs for URL or Upload */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    {t('productImage') || 'Product Image'}
                  </Label>
                  <Tabs value={imageSource} onValueChange={setImageSource} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="url">{t('imageUrl') || 'URL'}</TabsTrigger>
                      <TabsTrigger value="upload">{t('uploadFile') || 'Upload File'}</TabsTrigger>
                    </TabsList>
                    <TabsContent value="url" className="mt-2">
                      <Input
                        placeholder="https://example.com/image.jpg"
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        data-testid="product-image-url-input"
                      />
                    </TabsContent>
                    <TabsContent value="upload" className="mt-2">
                      <div className="space-y-3">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={handleFileUpload}
                          className="hidden"
                          data-testid="product-image-file-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full rounded-xl"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                        >
                          {uploading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              {t('chooseFile') || 'Choose File'}
                            </>
                          )}
                        </Button>
                        {formData.image_url && imageSource === 'upload' && (
                          <p className="text-xs text-muted-foreground truncate">
                            {formData.image_url.split('/').pop()}
                          </p>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>

                  {/* Image preview */}
                  {formData.image_url && (
                    <div className="relative mt-3">
                      <img
                        src={formData.image_url}
                        alt="Product preview"
                        className="w-full h-32 object-cover rounded-xl border"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 rounded-full bg-background/80 hover:bg-background"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('productLink')}</Label>
                    <Input
                      placeholder="https://store.com/product"
                      value={formData.link}
                      onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                      data-testid="product-link-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('price')}</Label>
                    <Input
                      placeholder="€9.99"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      data-testid="product-price-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('linkedConditionsProducts')}</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border rounded-xl">
                    {conditions.map(condition => (
                      <div key={condition.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`cond-${condition.id}`}
                          checked={formData.condition_ids.includes(condition.id)}
                          onCheckedChange={() => toggleCondition(condition.id)}
                        />
                        <label htmlFor={`cond-${condition.id}`} className="text-sm">{getConditionName(condition)}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setIsOpen(false)}>
                    {t('cancel')}
                  </Button>
                  <Button type="submit" className="flex-1 rounded-full" data-testid="save-product-btn">
                    {editingProduct ? t('update') : t('create')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Products list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : products.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
              >
                <Card className="p-4 rounded-2xl overflow-hidden" data-testid={`product-item-${index}`}>
                  <div className="flex gap-4">
                    {product.image_url && (
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-20 h-20 object-cover rounded-xl bg-muted"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{product.name}</h3>
                      {product.price && (
                        <div className="flex items-center gap-1 text-primary font-bold mt-1">
                          <DollarSign className="w-3.5 h-3.5" />
                          {product.price.replace('$', '').replace('€', '')}
                        </div>
                      )}
                      {product.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{product.description}</p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {product.condition_ids?.slice(0, 3).map(condId => {
                          const cond = conditions.find(c => c.id === condId);
                          return cond ? (
                            <span key={condId} className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                              {getConditionName(cond)}
                            </span>
                          ) : null;
                        })}
                        {product.condition_ids?.length > 3 && (
                          <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded-full">
                            +{product.condition_ids.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3 pt-3 border-t">
                    {product.link && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full"
                        onClick={() => window.open(product.link, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        {t('view')}
                      </Button>
                    )}
                    <div className="flex-1" />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-full"
                      onClick={() => handleOpenDialog(product)}
                      data-testid={`edit-product-${index}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-full text-destructive"
                      onClick={() => handleDelete(product.id)}
                      data-testid={`delete-product-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="p-8 rounded-2xl text-center">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-2">{t('noProductsYet')}</h3>
            <p className="text-muted-foreground text-sm">{t('addProductsToRecommend')}</p>
          </Card>
        )}
      </motion.div>
    </div>
  );
};
